import React, { useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity,
  StyleSheet, SafeAreaView, TextInput,
} from 'react-native';
import { colors, spacing, radius } from '../theme';
import { Divider } from '../components/UI';
import { REGIONS, CONTINENTS } from '../i18n/regions';
import { WORLD_LANGUAGES, useI18n } from '../i18n';
import { FULLY_TRANSLATED } from '../i18n/worldLanguages';
import BackButton from '../components/BackButton';

const LANGUAGES = WORLD_LANGUAGES || {};

const CONTINENT_ORDER = ['asia', 'europe', 'americas', 'middle_east', 'africa'];

export default function RegionLanguageScreen({ navigation }) {
  const { t, lang, setLang, region, setRegionAndLang } = useI18n();
  const [tab,    setTab]    = useState('region');
  const [search, setSearch] = useState('');

  /* ── Regions grouped by continent ── */
  const grouped = CONTINENT_ORDER.map(cont => ({
    key:     cont,
    ...(CONTINENTS[cont] || {}),
    regions: Object.values(REGIONS || {}).filter(r =>
      r.continent === cont &&
      ((r.nameEn || '').toLowerCase().includes(search.toLowerCase()) ||
       (r.name   || '').toLowerCase().includes(search.toLowerCase()))
    ),
  })).filter(g => g.regions.length > 0);

  // ═══ N'afficher que les langues effectivement traduites ═══
  // Avant : 65 langues affichées dont seulement 9 réellement traduites
  // → l'utilisateur sélectionnait Polonais/Hindi/etc. et tout restait en
  // français (fallback). On filtre donc strictement sur FULLY_TRANSLATED.
  const langs = Object.entries(LANGUAGES).filter(([code, v]) => {
    if (!v) return false;
    if (!FULLY_TRANSLATED.includes(code)) return false;
    const q = (search || '').toLowerCase();
    if (!q) return true;
    return (v.label || v.name || '').toLowerCase().includes(q) ||
           (v.nativeName || '').toLowerCase().includes(q);
  });

  return (
    <SafeAreaView style={s.safe}>
      

      <View style={s.topbar}>
        <BackButton onPress={() => navigation.goBack()} />
        <Text style={s.title}>
          {tab === 'region' ? t('select_region') || 'Région & Langue' : t('select_language') || 'Langue'}
        </Text>
        <View style={{ width: 38 }} />
      </View>

      <Divider />

      {/* ── Tabs ── */}
      <View style={s.tabs}>
        {['region', 'language'].map(tabKey => (
          <TouchableOpacity
            key={tabKey}
            style={[s.tab, tab === tabKey && s.tabActive]}
            onPress={() => { setTab(tabKey); setSearch(''); }}
          >
            <Text style={[s.tabLabel, tab === tabKey && s.tabLabelActive]}>
              {tabKey === 'region'
                ? (t('region') || 'Région')
                : (t('language') || 'Langue')}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      <View style={s.tabLine}>
        <View style={[s.tabUnder, tab === 'region' ? { left: '0%' } : { left: '50%' }]} />
      </View>

      {/* ── Search ── */}
      <View style={s.searchWrap}>
        <TextInput
          style={s.searchInput}
          placeholder={tab === 'region' ? '🔍  搜索 / Rechercher...' : '🔍  Search...'}
          placeholderTextColor={colors.text.faint}
          value={search}
          onChangeText={setSearch}
        />
      </View>

      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>

        {/* ══ REGION TAB ══════════════════════════════════════ */}
        {tab === 'region' && grouped.map(group => (
          <View key={group.key} style={s.group}>
            <View style={s.groupHeader}>
              <Text style={s.groupIcon}>{group.icon}</Text>
              <Text style={s.groupLabel}>{group.label}</Text>
            </View>
            {group.regions.map(reg => {
              const isActive = region === reg.code;
              const platforms = reg.platforms || [];
              return (
                <TouchableOpacity
                  key={reg.code}
                  style={[s.regionCard, isActive && s.regionCardActive]}
                  onPress={() => setRegionAndLang(reg.code)}
                  activeOpacity={0.75}
                >
                  <Text style={s.regionFlag}>{reg.flag}</Text>
                  <View style={s.regionInfo}>
                    <View style={s.regionNameRow}>
                      <Text style={[s.regionName, isActive && { color: colors.accent.main }]}>
                        {reg.name}
                      </Text>
                      {reg.name !== reg.nameEn && (
                        <Text style={s.regionNameEn}> · {reg.nameEn}</Text>
                      )}
                    </View>
                    <View style={s.regionMeta}>
                      <Text style={s.regionCurrency}>{reg.currency} · {reg.symbol}</Text>
                    </View>
                    {/* Platform chips */}
                    <View style={s.platformChips}>
                      {platforms.slice(0, 4).map(pid => (
                        <View key={pid} style={s.chip}>
                          <Text style={s.chipText}>{pid.replace(/_[a-z]+$/, '').toUpperCase()}</Text>
                        </View>
                      ))}
                      {platforms.length > 4 && (
                        <View style={s.chip}>
                          <Text style={s.chipText}>+{platforms.length - 4}</Text>
                        </View>
                      )}
                    </View>
                  </View>
                  {isActive && (
                    <View style={s.checkMark}>
                      <Text style={s.checkText}>✓</Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        ))}

        {/* ══ LANGUAGE TAB ════════════════════════════════════ */}
        {tab === 'language' && (
          <View style={s.group}>
            {langs.map(([code, info]) => {
              if (!info) return null;
              const isActive = lang === code;
              return (
                <TouchableOpacity
                  key={code}
                  style={[s.langCard, isActive && s.langCardActive]}
                  onPress={() => setLang(code)}
                  activeOpacity={0.75}
                >
                  <Text style={s.langFlag}>{info.flag || '🌐'}</Text>
                  <View style={s.langInfo}>
                    <Text style={[s.langName, isActive && { color: colors.accent.main }]}>
                      {info.label || code}
                    </Text>
                    <Text style={s.langCode}>{code}</Text>
                  </View>
                  {info.rtl && (
                    <View style={s.rtlBadge}>
                      <Text style={s.rtlBadgeText}>RTL</Text>
                    </View>
                  )}
                  {isActive && (
                    <View style={s.checkMark}>
                      <Text style={s.checkText}>✓</Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe:          { flex: 1, backgroundColor: '#FFFFFF' },
  topbar:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: spacing.xl, paddingBottom: spacing.md },
  backBtn:       { width: 28, height: 28, alignItems: 'center', justifyContent: 'center' },
  backText:      { fontSize: 22, color: colors.text.secondary, lineHeight: 26 },
  title:         { fontSize: 11, letterSpacing: 3, color: colors.text.secondary, textTransform: 'uppercase' },

  tabs:          { flexDirection: 'row', paddingHorizontal: spacing.xl },
  tab:           { flex: 1, paddingVertical: 10, alignItems: 'center' },
  tabLabel:      { fontSize: 9, letterSpacing: 2.5, color: colors.text.faint, textTransform: 'uppercase' },
  tabLabelActive:{ color: colors.accent.main },
  tabLine:       { height: 0.5, backgroundColor: colors.bg.border, marginHorizontal: spacing.xl, position: 'relative', marginBottom: spacing.sm },
  tabUnder:      { position: 'absolute', top: -0.5, width: '50%', height: 1, backgroundColor: colors.accent.main },

  searchWrap:    { paddingHorizontal: spacing.xl, marginBottom: spacing.sm },
  searchInput:   { backgroundColor: '#FFFFFF', borderRadius: radius.sm, borderWidth: 0.5, borderColor: colors.bg.border, paddingHorizontal: spacing.md, paddingVertical: 9, fontSize: 11, letterSpacing: 0.5, color: colors.text.secondary },

  group:         { paddingHorizontal: spacing.xl, marginBottom: spacing.md },
  groupHeader:   { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8, marginTop: 4 },
  groupIcon:     { fontSize: 14 },
  groupLabel:    { fontSize: 8, letterSpacing: 3, color: colors.text.faint, textTransform: 'uppercase' },

  regionCard:    { flexDirection: 'row', alignItems: 'flex-start', gap: 10, backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md, marginBottom: 6 },
  regionCardActive: { borderColor: colors.accent.main, backgroundColor: colors.accent.ghost },
  regionFlag:    { fontSize: 22, marginTop: 2 },
  regionInfo:    { flex: 1 },
  regionNameRow: { flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap' },
  regionName:    { fontSize: 13, color: colors.text.secondary, letterSpacing: 0.3, fontWeight: '400' },
  regionNameEn:  { fontSize: 10, color: colors.text.faint, letterSpacing: 0.3 },
  regionMeta:    { marginTop: 2 },
  regionCurrency:{ fontSize: 9, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase' },
  platformChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 6 },
  chip:          { backgroundColor: colors.bg.surface, borderRadius: radius.pill, borderWidth: 0.5, borderColor: colors.bg.border, paddingHorizontal: 6, paddingVertical: 2 },
  chipText:      { fontSize: 7, letterSpacing: 1, color: colors.text.faint, textTransform: 'uppercase' },
  checkMark:     { width: 20, height: 20, borderRadius: 10, backgroundColor: colors.accent.ghost, borderWidth: 0.5, borderColor: colors.accent.main, alignItems: 'center', justifyContent: 'center', marginTop: 2 },
  checkText:     { fontSize: 10, color: colors.accent.main, fontWeight: '500' },

  langCard:      { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 0.5, borderColor: colors.bg.border, padding: spacing.md, marginBottom: 6 },
  langCardActive:{ borderColor: colors.accent.main, backgroundColor: colors.accent.ghost },
  langFlag:      { fontSize: 20 },
  langInfo:      { flex: 1 },
  langName:      { fontSize: 13, color: colors.text.secondary, letterSpacing: 0.3 },
  langCode:      { fontSize: 9, letterSpacing: 2, color: colors.text.faint, textTransform: 'uppercase', marginTop: 2 },
  rtlBadge:      { backgroundColor: colors.bg.surface, borderRadius: radius.pill, borderWidth: 0.5, borderColor: colors.bg.border, paddingHorizontal: 6, paddingVertical: 2 },
  rtlBadgeText:  { fontSize: 8, letterSpacing: 1.5, color: colors.text.faint },
});
